import React from 'react';
import { Button, Col, Dropdown, Menu, Popconfirm, Row, Space } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MdOutlineUnpublished } from 'react-icons/md';
import { useIntl } from 'react-intl';
import { PiPencilSimpleLine, PiTrashSimple } from 'react-icons/pi';
import { RiArrowLeftLine, RiErrorWarningLine } from 'react-icons/ri';
import { DownOutlined } from '@ant-design/icons';
import { HasRights, PrivateComponent } from '../../../../../../components/HasRights/HasRights';
import IntlMessages from '../../../../../../layout/components/lang/IntlMessages';
import urlPageLocalNotificationEdit from '../../../../../../urls/urlPageLocalNotificationEdit';
import urlPageLocalNotifications from '../../../../../../urls/urlPageLocalNotifications';
import { Permissions } from '../../../../../../const/permissions';
import handleResponse from '../../../../../../lib/handleResponse';
import mkPreparedTemplateData from '../../../../../Content/utils/mkPreparedTemplateData';
import BreadCrumbs from '../../../../../../layout/components/breadcrumbs';
import ActionButton from '../../../../../../layout/components/action-button';
import NotificationDetailsCard from '../../../../../Content/components/NotificationDetailsCard';
import { EMPTY_VALUE_PLACEHOLDER } from '../../../../../../const/system';
import { TYPE_DICTIONARY } from '../../../../../Template builder/const/templates';
import { useGetDictionaryQuery } from '../../../../../Translations/api/dictionariesApiSlice';
import { useGetLanguagesQuery } from '../../../../../Translations/api/languagesApiSlice';
import {
  useDeleteLocalNotificationMutation,
  useGetSpecificLocalNotificationQuery,
  useUnpublishLocalNotificationMutation,
} from '../../../../api/localNotificationsApiSlice';



const PageLocalNotificationDetails = () => {
  const { notificationId } = useParams();
  const intl = useIntl();
  const navigate = useNavigate();

  const { data: localNotification = {}, isFetching } = useGetSpecificLocalNotificationQuery({ notificationId, includes: '?include=dynamic_data' });

  const dictionaryField = localNotification?.dynamic_data?.data?.find((item) => item.type === TYPE_DICTIONARY);
  const isPublished = Boolean(localNotification?.is_published);

  const { data: dictionary = {} } = useGetDictionaryQuery({
    id: dictionaryField?.value,
    include: 'translations,structure,structure.fields.translations',
  }, { skip: !dictionaryField?.value });

  const { data: {
    data: languages = [],
  } = { data: [] } } = useGetLanguagesQuery({ queryParams: 'limit=0' }, { skip: !dictionaryField });

  const [ unpublish, { isLoading: isUnpublishLocalNotificationLoading } ] = useUnpublishLocalNotificationMutation();
  const [ deleteLocalNotification, { isLoading: isLocalNotificationDeleting } ] = useDeleteLocalNotificationMutation();

  const data = mkPreparedTemplateData({
    templateData: localNotification?.template?.data?.fields?.data,
    dynamicData: localNotification?.dynamic_data?.data,
    dictionary,
    languages,
  });

  const handleUnpublish = () => {
    unpublish(notificationId)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-unpublished-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-unpublish-failed', [], error));
  };

  const handleDelete = (id) => {
    deleteLocalNotification(id)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-deleted-successfully', [ () => navigate(urlPageLocalNotifications()) ]))
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };


  const allowRenderActions = HasRights([ Permissions.OTHER.NOTIFICATIONS.LOCAL.EDIT ]) ||
    (isPublished && HasRights([ Permissions.OTHER.NOTIFICATIONS.LOCAL.UNPUBLISH ])) ||
    (!isPublished && HasRights([ Permissions.OTHER.NOTIFICATIONS.LOCAL.DELETE ]));

  const actionItems = (
    <Menu>
      <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.LOCAL.EDIT ]}>
        <Menu.Item
          key="edit"
          icon={<PiPencilSimpleLine size={18} />}
          onClick={() => navigate(urlPageLocalNotificationEdit({ notificationId: localNotification.id }))}
        >
          <IntlMessages id="ui-general-edit" />
        </Menu.Item>
      </PrivateComponent>
      {isPublished ? (
        <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.LOCAL.UNPUBLISH ]}>
          <Menu.Item
            key="unpublish"
            icon={<MdOutlineUnpublished size={18} />}
            onClick={handleUnpublish}
          >
            <IntlMessages id="ui-general-unpublish" />
          </Menu.Item>
        </PrivateComponent>
      ) : (
        <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.LOCAL.DELETE ]}>
          <Popconfirm
            title={<IntlMessages id="ui-general-delete-confirm-message" />}
            placement="topLeft"
            onConfirm={() => handleDelete(localNotification.id)}
            okText={<IntlMessages id="ui-general-yes" />}
            cancelText={<IntlMessages id="ui-general-no" />}
            icon={<RiErrorWarningLine className="remix-icon hp-text-color-primary-1" />}
            okButtonProps={{ danger: true }}
          >
            <Menu.Item
              key="delete"
              icon={<PiTrashSimple size={18} />}
              onClick={() => {}}
            >
              <IntlMessages id="ui-general-delete" />
            </Menu.Item>
          </Popconfirm>
        </PrivateComponent>
      )}
    </Menu>
  );

  return (
    <>
      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPageLocalNotifications()}>
              <IntlMessages id='local-notifications-breadcrumbs' />
            </Link>
          }
          breadCrumbActive={localNotification?.id ?? EMPTY_VALUE_PLACEHOLDER}
        />

        <Col md={10} span={24} className='text-end'>
          <Space size='middle'>
            {allowRenderActions && (
              <Dropdown overlay={actionItems}>
                <Button onClick={(ev) => ev.preventDefault()}>
                  <Space size='middle'>
                    <IntlMessages id='ui-general-action' />
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            )}

            <ActionButton
              title={intl.formatMessage({ id: 'ui-general-back-to-list' })}
              icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
              onClick={() => navigate(urlPageLocalNotifications())}
            />
          </Space>
        </Col>
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <NotificationDetailsCard
            title={<IntlMessages id='structures-source-local-notification' />}
            isLocalNotification
            data={localNotification}
            isLoadingCard={isUnpublishLocalNotificationLoading || isLocalNotificationDeleting}
            isFetching={isFetching}
            dictionary={dictionary}
            languages={languages}
            templateCardsData={data}
          />
        </Col>
      </Row>
    </>
  );
};

export default PageLocalNotificationDetails;
